/**
 * Public order summary kind: marketplace sales vs installation job vs removal job.
 */

export type PublicOrderNormalizeKind = "sales" | "installation" | "removal";

/** Backend may wrap job payloads as { installation_order | removal_order, project_settings }. */
export function normalizeFetchedOrderShape(data: any, kind: PublicOrderNormalizeKind): any {
  if (!data) return null;
  if (kind === "installation") {
    if (data?.installation_order && typeof data.installation_order === "object") {
      return {
        ...data.installation_order,
        project_settings: data.project_settings ?? data.installation_order?.project_settings,
      };
    }
    return data;
  }
  if (kind === "removal") {
    if (data?.removal_order && typeof data.removal_order === "object") {
      return {
        ...data.removal_order,
        project_settings: data.project_settings ?? data.removal_order?.project_settings,
      };
    }
    return data;
  }
  return data;
}

export function computeInstallationPublicSummary(
  pathname: string,
  isPrivateOrderSummaryPreview: boolean,
  previewOrder: any | undefined
): boolean {
  const path = pathname.toLowerCase().includes("/installationsummary");
  const previewType = String(
    previewOrder?.order_type || previewOrder?.type || ""
  ).toLowerCase();
  const privateInstallPreview =
    isPrivateOrderSummaryPreview &&
    previewOrder &&
    (
      previewType === "installation" ||
      previewType === "installation_order" ||
      previewType.includes("install") ||
      (!!previewOrder?.installation_details && !previewOrder?.removal_details)
    );
  return path || privateInstallPreview;
}

export function computeRemovalPublicSummary(
  pathname: string,
  isPrivateOrderSummaryPreview: boolean,
  previewOrder: any | undefined
): boolean {
  const p = pathname.toLowerCase();
  const path = p.includes("/removalsummary") || p.includes("/removalordersummary");
  const privateRemovalPreview =
    isPrivateOrderSummaryPreview &&
    previewOrder &&
    String(previewOrder.order_type || "").toLowerCase() === "removal";
  return path || privateRemovalPreview;
}

export function publicOrderNormalizeKindFromFlags(
  isRemovalPublicSummary: boolean,
  isInstallationPublicSummary: boolean
): PublicOrderNormalizeKind {
  if (isRemovalPublicSummary) return "removal";
  if (isInstallationPublicSummary) return "installation";
  return "sales";
}

/** Normalize Mongo-shaped ids and plain strings from order payloads. */
export function normalizeOrderUserId(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "object" && v !== null && "$oid" in (v as Record<string, unknown>)) {
    return String((v as { $oid?: string }).$oid ?? "").trim();
  }
  if (typeof v === "object" && v !== null && "id" in (v as Record<string, unknown>)) {
    return String((v as { id?: unknown }).id ?? "").trim();
  }
  const s = String(v).trim();
  return s === "[object Object]" ? "" : s;
}

/**
 * Signage supplier user id on a sales order (invoice template / logo).
 * Prefer explicit signage fields — `created_by` is often the agent/realtor buyer.
 */
export function resolveSignageIdFromOrder(order: unknown): string {
  if (!order || typeof order !== "object") return "";
  const po = order as Record<string, unknown>;
  const signage = po.signage as { id?: unknown; _id?: unknown } | undefined;
  const fromSignageNested =
    signage && typeof signage === "object"
      ? normalizeOrderUserId(signage._id) || normalizeOrderUserId(signage.id)
      : "";

  const direct =
    normalizeOrderUserId(po.signage_id) ||
    fromSignageNested ||
    normalizeOrderUserId(po.seller_owner_node_id) ||
    normalizeOrderUserId(po.supplier_id) ||
    normalizeOrderUserId(po.user_id);

  if (direct) return direct;

  const cb = po.created_by as { id?: unknown } | undefined;
  return cb && typeof cb === "object" ? normalizeOrderUserId(cb.id) : "";
}
